import merge from 'lodash-es/merge'

import MapFeature from './MapFeature'
import MapContent from './MapContent'

const getSelectionId = originalId => originalId + '-selection'

const getGeoJsonFeatureCollection = features => ({
    type: 'FeatureCollection',
    features,
})

/**
 * @typedef {Object} FeatureSelectionConfig
 * @property {string} sourceId
 * @property {Object} layerAdaption Parts of the layer definition that should differ for selection
 * @property {string} featureIdKey Key in feature properties to use as id
 */

/**
 * @typedef {Object} GeoJsonFeature
 * @property {"Feature"} type
 * @property {Object} properties
 * @property {Object} geometry
 */

/**
 * Shows a dynamic selection of features on another layer instead of the original one.
 */
export default class FeatureSelection extends MapFeature {

    /** @type {string} */
    _sourceId

    /** @type {Object} */
    _layerAdaption

    /** @type {string[]} */
    _originalLayerIds

    /** @type {string} */
    _featureIdKey

    /** @type {GeoJsonFeature[]} */
    _selectionGeoJson = []

    /** @type {MapContent} */
    _mapContent

    /**
     * @param {FeatureSelectionConfig} config
     * @param {...string[]} layerIds
     */
    constructor({ sourceId, layerAdaption, featureIdKey }, ...layerIds) {
        super()

        this._sourceId = getSelectionId(sourceId)
        this._layerAdaption = layerAdaption
        this._originalLayerIds = layerIds
        this._featureIdKey = featureIdKey
    }

    /**
     * Show a selection of features on the map.
     * @param {GeoJsonFeature[]} features
     */
    setSelection(features) {
        const previousSelectionCount = this._selectionGeoJson.length
        this._selectionGeoJson = features

        if (this._map) {
            this._hideSelectedOriginalFeatures(previousSelectionCount)
            this._mapContent.setData(getGeoJsonFeatureCollection(this._selectionGeoJson))
        }
    }

    /** @type {MapContent} */
    get content() { return this._mapContent }

    _onAdd(map) {
        this._initMapContent().addTo(map)

        if (this._selectionGeoJson.length) {
            this._hideSelectedOriginalFeatures(0)
        }
    }

    _onRemove() {
        this._mapContent.remove()
    }

    /**
     * Add source and layers for selected features to the map.
     * @returns {MapContent}
     */
    _initMapContent() {
        this._mapContent = new MapContent(this._sourceId, {
            type: 'geojson',
            data: getGeoJsonFeatureCollection(this._selectionGeoJson),
        }, ...this._createSelectionLayers())
        return this._mapContent
    }

    /**
     * Copy and adapt each layer by given IDs for selection.
     * @returns {import("maplibre-gl").AnyLayer[]}
     */
    _createSelectionLayers() {
        return this._getOriginalLayers().map(originalLayer => {
            const selectionLayer = merge({}, originalLayer, this._layerAdaption, {
                id: getSelectionId(originalLayer.id),
            })
            delete selectionLayer['source-layer']
            return selectionLayer
        })
    }

    /**
     * @returns {import("maplibre-gl").AnyLayer[]}
     */
    _getOriginalLayers() {
        return this._map.getStyle().layers.filter(
            layer => this._originalLayerIds.includes(layer.id)
        )
    }

    /**
     * Update filters to hide the selected features on the original layers.
     * @param {number} previousSelectionCount Number of features that have been selected before
     */
    _hideSelectedOriginalFeatures(previousSelectionCount) {
        const selectionFilters = this._getFiltersForCurrentSelection()

        // Update filters of original layers to hide selected pois
        for (const layer of this._getOriginalLayers()) {
            this._replaceLayerFilters(layer, previousSelectionCount, selectionFilters)
        }
    }

    /**
     * Create filters that excluce features like in the current selection.
     * @returns {Array[]} Array of maplibre filters
     */
    _getFiltersForCurrentSelection() {
        return this._selectionGeoJson.map(feature => {
            const id = feature.properties[this._featureIdKey]
            // Create a filter rule to hide features with this ID
            return ['!=', this._featureIdKey, id]
        })
    }

    /**
     * Replace a given number of filters in the layer with new ones.
     * @param {{ filter }} layer
     * @param {number} oldFiltersCount
     * @param {Array[]} newFilters
     */
    _replaceLayerFilters(layer, oldFiltersCount, newFilters) {
        // Prepare filter array from current filter
        const filter = layer.filter ? [...layer.filter] : ['all']
        // Replace the previous number of filters with new ones
        filter.splice(1, oldFiltersCount, ...newFilters)
        // Apply new filter array to the layer
        this._map.setFilter(layer.id, filter)
    }

}
